// Auth domain configuration. The legacy code had `https://beta.creativetoolbox.ai`
// and `.creativetoolbox.ai` hardcoded as fallbacks, which (a) made every local
// `npm run dev` reach across the public internet, and (b) meant we couldn't
// ship a build that talked to a staging SSO domain without code changes.
//
// In v3, both come from env. When `VITE_CENTRAL_APP_URL` is unset, central-SSO
// integration is suppressed entirely and the app falls back to the
// /dev-auth-helper paste-token flow (spec §4.4).

/**
 * Registrable domain of a hostname, as the last two labels.
 *
 * Adequate for the domains this app is served from (creativetoolbox.ai,
 * smartai.africa, coralcreator.com). It would be wrong for a multi-part TLD
 * like `co.uk`, which would need the public suffix list — add that only if we
 * ever serve from one.
 */
function parentDomainOf(hostname: string): string {
  return hostname.split('.').slice(-2).join('.');
}

/**
 * Central SSO origin for the host we are currently served from.
 *
 * One build answers on several domains, so a single origin cannot be right for
 * all of them. The session cookie is only readable by hosts sharing a parent
 * domain with the central app, so we prefer a configured origin on the same
 * parent as the current host — that keeps sign-in silent where a cookie can
 * reach, and falls back to the primary origin elsewhere, where the token
 * handoff through /sync-session takes over.
 *
 * Concretely: imagelab.creativetoolbox.ai talks to the SSO on
 * beta.creativetoolbox.ai and stays silently signed in, while coralcreator.com
 * talks to auth.smartai.africa and is signed in by the handoff.
 */
export function getCentralAppUrl(): string | null {
  const configured = [
    import.meta.env.VITE_CENTRAL_APP_URL?.trim(),
    import.meta.env.VITE_CENTRAL_APP_URL_ALT?.trim(),
  ].filter((u): u is string => Boolean(u && u.length > 0));

  if (configured.length === 0) return null;
  if (typeof window === 'undefined') return configured[0];

  const hereParent = parentDomainOf(window.location.hostname);
  const sameParent = configured.find(url => {
    try {
      return parentDomainOf(new URL(url).hostname) === hereParent;
    } catch {
      return false;
    }
  });
  return sameParent ?? configured[0];
}

export function getCentralAppOrigin(): string | null {
  const url = getCentralAppUrl();
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    console.warn('VITE_CENTRAL_APP_URL is set but not a valid URL:', url);
    return null;
  }
}

/**
 * Cookie `domain` attribute. When unset (local dev), js-cookie defaults to
 * the document's host — which is the safe behavior for `localhost`.
 */
export function getAuthCookieDomain(): string | undefined {
  const raw = import.meta.env.VITE_AUTH_COOKIE_DOMAIN?.trim();
  return raw && raw.length > 0 ? raw : undefined;
}

export function isCentralSsoEnabled(): boolean {
  return getCentralAppUrl() !== null;
}

/**
 * The central app's sync-session page, for the hidden iframe. Names this app's
 * origin so the central page posts the session here — its fallback list of
 * sibling origins predates Coral Creator and doesn't include it. The central
 * page still checks the origin against its own allow-list.
 */
export function centralSyncFrameUrl(centralAppUrl: string): string {
  return `${centralAppUrl}/sync-session?origin=${encodeURIComponent(window.location.origin)}`;
}

/**
 * Dev-only auth bypass. When `VITE_DEV_AUTH_BYPASS=true` AND we're running a
 * Vite dev build (`import.meta.env.DEV`), the app boots with a hardcoded
 * dev JWT pre-seeded into the token storage so the user lands on the lab
 * without needing to paste a real SSO token. Returns `false` in any
 * production build regardless of the env var — belt-and-suspenders so a
 * stray `.env.production` setting can't accidentally ship a bypassed auth
 * gate to staging or prod.
 *
 * The BE accepts any well-formed JWT in Development (signature + lifetime
 * + issuer + audience validation are all disabled — see
 * `ssgp-v3-agents-simple/Program.cs` line ~142), and the v3 controllers
 * don't carry `[Authorize]`, so a synthetic token passes through.
 */
export function isDevAuthBypassEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  const raw = import.meta.env.VITE_DEV_AUTH_BYPASS;
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}
